/**
 * MermaidBlockView — the node view of a mermaid fence in the page editor
 * (FRIDAY-67).
 *
 * The author sees the diagram; the source is one click away. The source
 * stays the editor's real text node, so typing, selection, undo/redo and
 * the save debounce keep working exactly as they do in a code block.
 */

import { useEffect, useRef, useState } from 'react'
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react'
import { MermaidDiagram } from '@/lib/mermaid/MermaidDiagram'
import { cn } from '@/lib/utils'

type Mode = 'diagram' | 'source'

export function MermaidBlockView({ node, editor, getPos }: NodeViewProps) {
  const source = node.textContent
  const [mode, setMode] = useState<Mode>('diagram')
  const [failed, setFailed] = useState(false)
  const inside = useRef(false)

  // A new source gets a fresh chance to render.
  useEffect(() => {
    setFailed(false)
  }, [source])

  // When the caret moves into the block, the author is about to type — show
  // the source. Moving out leaves the mode alone.
  useEffect(() => {
    const isInside = () => {
      const pos = getPos()
      if (typeof pos !== 'number') return false
      const { from, to } = editor.state.selection
      return from > pos && to < pos + node.nodeSize
    }
    const onSelectionUpdate = () => {
      const nowInside = isInside()
      if (nowInside && !inside.current) setMode('source')
      inside.current = nowInside
    }
    editor.on('selectionUpdate', onSelectionUpdate)
    // The caret can already be inside the block when it appears (a page
    // that ends with a diagram puts the caret in it). That is not the
    // author asking to edit it, so the diagram stays.
    inside.current = isInside()
    return () => {
      editor.off('selectionUpdate', onSelectionUpdate)
    }
  }, [editor, getPos, node.nodeSize])

  const showSource = () => {
    setMode('source')
    const pos = getPos()
    if (typeof pos !== 'number') return
    try {
      editor.commands.focus()
      editor.commands.setTextSelection(pos + 1)
    } catch {
      // The block moved while we were clicking — the source is visible
      // either way.
    }
  }

  const showDiagram = () => {
    setMode('diagram')
    // The caret must not stay inside a block whose source is hidden, or
    // typing would go into an invisible text node.
    const pos = getPos()
    if (typeof pos !== 'number') return
    const { from, to } = editor.state.selection
    if (from > pos && to < pos + node.nodeSize) {
      try {
        editor.commands.setTextSelection(pos + node.nodeSize)
      } catch {
        // Nothing to move — the block is the last one.
      }
    }
  }

  // A diagram that cannot be rendered shows its message *and* the source,
  // so the author can fix it.
  const sourceVisible = mode === 'source' || failed

  return (
    <NodeViewWrapper className="friday-mermaid-block" data-mermaid-block="">
      <div className="friday-mermaid-block-bar" contentEditable={false}>
        <button
          type="button"
          className={cn('friday-mermaid-block-tab', mode === 'diagram' && 'is-active')}
          aria-pressed={mode === 'diagram'}
          onClick={showDiagram}
        >
          Diagram
        </button>
        <button
          type="button"
          className={cn('friday-mermaid-block-tab', mode === 'source' && 'is-active')}
          aria-pressed={mode === 'source'}
          onClick={showSource}
        >
          Source
        </button>
      </div>

      <div className="friday-mermaid-block-preview" contentEditable={false}>
        <MermaidDiagram
          source={source}
          showSource={false}
          onActivate={showSource}
          onError={() => setFailed(true)}
        />
      </div>

      <pre
        className={cn('friday-mermaid-block-source', !sourceVisible && 'friday-mermaid-block-source--hidden')}
      >
        <NodeViewContent as="code" style={{ whiteSpace: 'pre' }} />
      </pre>
    </NodeViewWrapper>
  )
}
